<script setup lang="ts">
import CommonButton from "@/components/common/CommonButton.vue";
import CommonSearch from "@/components/common/CommonSearch.vue";
import CommonTable from "@/components/common/CommonTable.vue";
import { useShiftStore } from "@/features/shifts/shifts.store";
import { IShiftTemplate } from "@/features/shifts/shifts.types";
import { Pencil02, Trash01, SearchMd, Plus } from "utruckin-icons";
import { onMounted, ref } from "vue";
import CommonDropdown from "@/components/common/CommonDropdown.vue";
import { useAppStore } from "@/store/app.store";
import { watch } from "vue";

const shiftStore = useShiftStore();
const appStore = useAppStore();

const loading = ref(false);
const shifts = ref<IShiftTemplate[]>([]);
const search = ref<string>("");

const getList = async () => {
  shifts.value = await shiftStore.getShifts(loading);
};

onMounted(() => {
  getList();
});

watch(
  () => appStore.refreshKey,
  () => {
    getList();
  },
  {
    immediate: false,
  }
);
</script>

<template>
  <div class="v-shift">
    <div class="v-shift__header">
      <CommonSearch
        v-model="search"
        :icon="SearchMd"
        :placeholder="'Search'"
        clearable
      />
      <CommonButton
        type="primary"
        label="Add Shift"
        :prepend="true"
        :prepend-icon="Plus"
      />
    </div>
    <CommonTable
      :headers="shiftStore.shiftHeaders"
      :items="shifts"
      :loading="loading"
    >
      <template #is_active="{ item }">
        <span
          class="v-shift__badge"
          :class="item.is_active ? 'active' : 'inactive'"
        >
          {{ item.is_active ? "Active" : "Inactive" }}
        </span>
      </template>

      <template #kpi>
        <span>—</span>
      </template>

      <template #kpi_progress>
        <span>-</span>
      </template>

      <template #actions>
        <div class="v-shift__actions">
          <button class="v-shift__btn">
            <Pencil02 :size="18" />
          </button>
          <button class="v-shift__btn">
            <Trash01 :size="18" />
          </button>
        </div>
      </template>
    </CommonTable>
  </div>
</template>

<style scoped lang="scss">
.v-shift {
  padding: var(--base-space-24);
  background: var(--color-system-background);
  overflow: auto;
  height: calc(100vh - 80px);

  &__header {
    display: flex;
    align-items: center;
    justify-content: end;
    gap: var(--base-space-8);
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-label-secondary);
    line-height: 20px;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--base-space-4) var(--base-space-8);
    gap: var(--base-space-8);
    border-radius: var(--base-radius-8);
    color: var(--color-label);
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;

    &.active {
      background-color: var(--color-green-light);
    }

    &.inactive {
      background-color: var(--color-red-light);
    }
  }

  &__actions {
    display: flex;
    gap: var(--base-space-8);
  }

  &__btn {
    cursor: pointer;
    border: 1px solid transparent;
    transition: opacity 0.2s;
    background-color: var(--color-secondary-system-background);
    color: var(--color-label);
    border-color: var(--color-border);
    display: flex;
    justify-content: center;
    align-items: center;
    gap: var(--base-space-8);
    width: 32px;
    height: 32px;
    padding: 6px;
    border-radius: var(--base-radius-12);

    &:hover {
      opacity: 0.8;
    }
  }
}
</style>
